import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tab, Tabs, Form, Table, Button, Spinner } from "react-bootstrap";
import Navbar from "./Navbar";
import API_CONFIG, { buildApiUrl, buildApiUrlWithParams } from '../config/api';

const Promotions = () => {
  const [groups, setGroups] = useState("");
  const [activeTab, setActiveTab] = useState("testing-events");
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [selectedEventData, setSelectedEventData] = useState(null);
  const [studentRegistrations, setStudentRegistrations] = useState([]);
  const [beltRanks, setBeltRanks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Bulk search states
  const [bulkSearchResults, setBulkSearchResults] = useState([]);
  const [bulkSearchLoading, setBulkSearchLoading] = useState(false);
  const [bulkSearchFilters, setBulkSearchFilters] = useState({
    first_name: "",
    last_name: "",
    current_rank: "",
    is_junior: "all",
    time_since_promotion: "all"
  });
  const [promotionDate, setPromotionDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const token = localStorage.getItem("token"); // Retrieve token

    if (!token) {
      console.error("No authentication token found.");
      return;
    } else {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setGroups(decoded["cognito:groups"]);
    }
  }, []);

  useEffect(() => {
    if (groups && groups.includes("mtkd-admin")) {
      fetchEvents();
      fetchBeltRanks();
    }
  }, [groups]);

  useEffect(() => {
    if (selectedEvent) {
      fetchStudentRegistrations(selectedEvent);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.EVENTS),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const allEvents = await response.json();
        // Filter for active Belt Test events
        const beltTestEvents = allEvents.filter(event => 
          event.active === true && event.event_type_name === "Belt Test"
        );
        setEvents(beltTestEvents);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  const fetchBeltRanks = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.RANKS),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const ranks = await response.json();
        setBeltRanks(ranks);
      }
    } catch (error) {
      console.error("Error fetching belt ranks:", error);
    }
  };

  const fetchStudentRegistrations = async (eventId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      // Ensure eventId is properly converted to string for the API call
      const response = await fetch(
        buildApiUrlWithParams(API_CONFIG.ENDPOINTS.EVENT_REGISTRATIONS, eventId.toString()),
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const registrations = await response.json();
        const studentsWithDetails = await Promise.all(
          registrations.map(async (reg) => {
            // Get student details
            const studentResponse = await fetch(
              buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_DETAIL, reg.student_id),
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            
            if (studentResponse.ok) {
              const student = await studentResponse.json();
              // Check if student was already promoted at this event date
              const isReadOnly = student.date_last_promoted && 
                new Date(student.date_last_promoted).toISOString().split('T')[0] === 
                new Date(selectedEventData?.event_date).toISOString().split('T')[0];
              
              console.log(`Student ${student.first_name} ${student.last_name}:`, {
                date_last_promoted: student.date_last_promoted,
                event_date: selectedEventData?.event_date,
                isReadOnly: isReadOnly
              });
              
              return {
                ...reg,
                student_name: `${student.first_name} ${student.last_name}`,
                belt_rank: student.current_rank || "No Rank",
                stripes: student.current_stripes || "0",
                current_is_junior: student.is_junior || false,
                date_of_birth: student.date_of_birth,
                date_last_promotion: student.date_last_promoted || null,
                is_readonly: isReadOnly,
                updated_rank: "",
                updated_stripes: 0,
                updated_is_junior: false
              };
            }
            return reg;
          })
        );
        
        setStudentRegistrations(studentsWithDetails);
      }
    } catch (error) {
      console.error("Error fetching student registrations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEventChange = (eventId) => {
    setSelectedEvent(eventId);
    if (eventId) {
      // Convert eventId to the same type as event.event_id for comparison
      const eventData = events.find(event => event.event_id.toString() === eventId.toString());
      setSelectedEventData(eventData);
    } else {
      setSelectedEventData(null);
      setStudentRegistrations([]);
    }
  };

  const handleRankChange = (studentId, newRank) => {
    setStudentRegistrations(prev => 
      prev.map(student => {
        if (student.student_id === studentId) {
          // Calculate is_junior based on student's age at event date
          let isJunior = false;
          if (student.date_of_birth && selectedEventData?.event_date) {
            const birthDate = new Date(student.date_of_birth);
            const eventDate = new Date(selectedEventData.event_date);
            const age = eventDate.getFullYear() - birthDate.getFullYear();
            const monthDiff = eventDate.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && eventDate.getDate() < birthDate.getDate())) {
              age--;
            }
            isJunior = age < 16;
          }
          
          return { 
            ...student, 
            updated_rank: newRank, 
            updated_stripes: 0,
            updated_is_junior: isJunior
          };
        }
        return student;
      })
    );
  };

  const handleStripesChange = (studentId, newStripes) => {
    setStudentRegistrations(prev => 
      prev.map(student => 
        student.student_id === studentId 
          ? { ...student, updated_stripes: parseInt(newStripes) || 0 }
          : student
      )
    );
  };

  const handleIsJuniorChange = (studentId, isJunior) => {
    setStudentRegistrations(prev => 
      prev.map(student => 
        student.student_id === studentId 
          ? { ...student, updated_is_junior: isJunior }
          : student
      )
    );
  };

  const getNextRankOptions = (currentRank) => {
    if (!currentRank || currentRank === "No Rank") {
      return beltRanks.filter(rank => rank.rank === "WHITE");
    }
    
    const currentRankIndex = beltRanks.findIndex(r => r.rank === currentRank);
    if (currentRankIndex === -1) return [];
    
    // Include current rank and all higher ranks to allow stripe-only promotions
    return beltRanks.slice(currentRankIndex);
  };

  const getPromotionCount = () => {
    return studentRegistrations.filter(student => 
      !student.is_readonly && student.updated_rank
    ).length;
  };

  const handleSubmitPromotions = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const studentsToPromote = studentRegistrations.filter(
        student => !student.is_readonly && student.updated_rank
      );
      
      if (studentsToPromote.length === 0) {
        alert("No students selected for promotion");
        return;
      }
      
      // Process each promotion
      const results = await Promise.allSettled(
        studentsToPromote.map(async (student) => {
          const promotionData = {
            rank: student.updated_rank,
            stripes: student.updated_stripes,
            date_promoted: selectedEventData.event_date,
            is_junior: student.updated_is_junior,
            is_current: true
          };
          
          const response = await fetch(
            buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_PROMOTIONS, student.student_id),
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(promotionData)
            }
          );
          
          if (!response.ok) {
            throw new Error(`Failed to promote ${student.student_name}`);
          }
          
          return { student: student.student_name, success: true };
        })
      );
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed === 0) {
        alert(`Successfully promoted ${successful} students!`);
        // Refresh the student list
        fetchStudentRegistrations(selectedEvent);
      } else {
        alert(`Promoted ${successful} students, but ${failed} failed. Check console for details.`);
      }
      
    } catch (error) {
      console.error("Error submitting promotions:", error);
      alert("Failed to submit promotions");
    } finally {
      setSubmitting(false);
    }
  };

  // Bulk search functions
  const handleBulkSearchChange = (e) => {
    const { name, value } = e.target;
    setBulkSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const performBulkSearch = async () => {
    setBulkSearchLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        buildApiUrl(API_CONFIG.ENDPOINTS.STUDENTS),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch students");
      }
      
      const allStudents = await response.json();
      
      // Apply filters
      let filteredStudents = allStudents.filter(student => {
        const matchesFirstName = (student.first_name || "").toLowerCase().includes(bulkSearchFilters.first_name.toLowerCase());
        const matchesLastName = (student.last_name || "").toLowerCase().includes(bulkSearchFilters.last_name.toLowerCase());
        const matchesRank = (student.current_rank || "").toLowerCase().includes(bulkSearchFilters.current_rank.toLowerCase());
        const matchesJunior = bulkSearchFilters.is_junior === "all" || 
          (bulkSearchFilters.is_junior === "yes" && student.is_junior) || 
          (bulkSearchFilters.is_junior === "no" && !student.is_junior);
        
        // Time since promotion filter
        let matchesTimeFilter = true;
        if (bulkSearchFilters.time_since_promotion !== "all" && student.date_last_promoted) {
          const lastPromotionDate = new Date(student.date_last_promoted);
          const now = new Date();
          const daysSincePromotion = Math.floor((now - lastPromotionDate) / (1000 * 60 * 60 * 24));
          
          switch (bulkSearchFilters.time_since_promotion) {
            case "30":
              matchesTimeFilter = daysSincePromotion <= 30;
              break;
            case "60":
              matchesTimeFilter = daysSincePromotion <= 60;
              break;
            case "90":
              matchesTimeFilter = daysSincePromotion <= 90;
              break;
            case "180":
              matchesTimeFilter = daysSincePromotion <= 180;
              break;
            case "365":
              matchesTimeFilter = daysSincePromotion <= 365;
              break;
            case "730":
              matchesTimeFilter = daysSincePromotion <= 730;
              break;
            default:
              matchesTimeFilter = true;
          }
        }

        return matchesFirstName && matchesLastName && matchesRank && matchesJunior && matchesTimeFilter;
      });

      // Transform students to match the format expected by the table
      const transformedStudents = filteredStudents.map(student => {
        // Calculate if student is junior based on age at promotion date
        let isJunior = false;
        if (student.date_of_birth && promotionDate) {
          const birthDate = new Date(student.date_of_birth);
          const eventDate = new Date(promotionDate);
          const age = eventDate.getFullYear() - birthDate.getFullYear();
          const monthDiff = eventDate.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && eventDate.getDate() < birthDate.getDate())) {
            age--;
          }
          isJunior = age < 16;
        }
        
        return {
          student_id: student.student_id,
          student_name: `${student.first_name} ${student.last_name}`,
          belt_rank: student.current_rank || "No Rank",
          stripes: student.current_stripes || "0",
          current_is_junior: student.is_junior || false,
          date_of_birth: student.date_of_birth,
          date_last_promotion: student.date_last_promoted || null,
          updated_rank: "",
          updated_stripes: 0,
          updated_is_junior: isJunior
        };
      });

      setBulkSearchResults(transformedStudents);
    } catch (error) {
      console.error("Error performing bulk search:", error);
    } finally {
      setBulkSearchLoading(false);
    }
  };

  const handleBulkRankChange = (studentId, newRank) => {
    setBulkSearchResults(prev => 
      prev.map(student => {
        if (student.student_id === studentId) {
          // Calculate is_junior based on student's age at promotion date
          let isJunior = false;
          if (student.date_of_birth && promotionDate) {
            const birthDate = new Date(student.date_of_birth);
            const eventDate = new Date(promotionDate);
            const age = eventDate.getFullYear() - birthDate.getFullYear();
            const monthDiff = eventDate.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && eventDate.getDate() < birthDate.getDate())) {
              age--;
            }
            isJunior = age < 16;
          }
          
          return { 
            ...student, 
            updated_rank: newRank, 
            updated_stripes: 0,
            updated_is_junior: isJunior
          };
        }
        return student;
      })
    );
  };

  const handleBulkStripesChange = (studentId, newStripes) => {
    setBulkSearchResults(prev => 
      prev.map(student => 
        student.student_id === studentId 
          ? { ...student, updated_stripes: parseInt(newStripes) || 0 }
          : student
      )
    );
  };

  const handleBulkIsJuniorChange = (studentId, isJunior) => {
    setBulkSearchResults(prev => 
      prev.map(student => 
        student.student_id === studentId 
          ? { ...student, updated_is_junior: isJunior }
          : student
      )
    );
  };

  const getBulkPromotionCount = () => {
    return bulkSearchResults.filter(student => student.updated_rank).length;
  };

  const handleBulkSubmitPromotions = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const studentsToPromote = bulkSearchResults.filter(student => student.updated_rank);
      
      if (studentsToPromote.length === 0) {
        alert("No students selected for promotion");
        return;
      }
      
      // Process each promotion
      const results = await Promise.allSettled(
        studentsToPromote.map(async (student) => {
          const promotionData = {
            rank: student.updated_rank,
            stripes: student.updated_stripes,
            date_promoted: promotionDate, // Use selected promotion date
            is_junior: student.updated_is_junior,
            is_current: true
          };
          
          const response = await fetch(
            buildApiUrlWithParams(API_CONFIG.ENDPOINTS.STUDENT_PROMOTIONS, student.student_id),
            {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(promotionData)
            }
          );
          
          if (!response.ok) {
            throw new Error(`Failed to promote ${student.student_name}`);
          }
          
          return { student: student.student_name, success: true };
        })
      );
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      if (failed === 0) {
        alert(`Successfully promoted ${successful} students!`);
        // Refresh the search results
        performBulkSearch();
      } else {
        alert(`Promoted ${successful} students, but ${failed} failed. Check console for details.`);
      }
      
    } catch (error) {
      console.error("Error submitting bulk promotions:", error);
      alert("Failed to submit promotions");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <Navbar groups={groups} />
      <div className="card p-3 shadow-lg">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="text-primary mb-0">Promotions</h2>
        </div>
        
        <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
          <Tab eventKey="testing-events" title="Testing Events">
            <div className="p-3">
              <Form.Group className="mb-3">
                <Form.Label>Select Belt Test Event:</Form.Label>
                <Form.Select
                  value={selectedEvent}
                  onChange={(e) => handleEventChange(e.target.value)}
                >
                  <option value="">Choose an event...</option>
                  {events.map(event => (
                    <option key={event.event_id} value={event.event_id}>
                      {event.event_name} - {new Date(event.event_date).toLocaleDateString()}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              
              {selectedEventData && (
                <div className="alert alert-info">
                  <strong>Selected Event:</strong> {selectedEventData.event_name}<br/>
                  <strong>Event ID:</strong> {selectedEventData.event_id}<br/>
                  <strong>Event Date:</strong> {new Date(selectedEventData.event_date).toLocaleDateString()}
                </div>
              )}

              {selectedEvent && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Student Registrations</h4>
                    <Button
                      variant="primary"
                      onClick={handleSubmitPromotions}
                      disabled={getPromotionCount() === 0 || submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        `Submit Promotions (${getPromotionCount()})`
                      )}
                    </Button>
                  </div>

                  {loading ? (
                    <div className="text-center p-4">
                      <Spinner animation="border" />
                      <p className="mt-2">Loading students...</p>
                    </div>
                  ) : (
                    <Table striped bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Student Name</th>
                          <th>Belt Rank</th>
                          <th>Stripes</th>
                          <th>Current Is Junior</th>
                          <th>Date of Last Promotion</th>
                          <th>Updated Rank</th>
                          <th>Updated Stripes</th>
                          <th>Updated Is Junior</th>
                        </tr>
                      </thead>
                      <tbody>
                        {studentRegistrations.map((student) => (
                          <tr key={student.student_id} className={student.is_readonly ? 'table-secondary' : ''}>
                            <td>{student.student_name}</td>
                            <td>{student.belt_rank}</td>
                            <td>{student.stripes}</td>
                            <td>
                              <span className={`badge ${student.current_is_junior ? 'bg-success' : 'bg-secondary'}`}>
                                {student.current_is_junior ? 'Yes' : 'No'}
                              </span>
                            </td>
                            <td>
                              {student.date_last_promotion 
                                ? new Date(student.date_last_promotion).toLocaleDateString()
                                : "Never"
                              }
                            </td>
                            <td>
                              {student.is_readonly ? (
                                <span className="text-muted">Already promoted</span>
                              ) : (
                                <Form.Select
                                  value={student.updated_rank}
                                  onChange={(e) => handleRankChange(student.student_id, e.target.value)}
                                >
                                  <option value="">Select rank...</option>
                                  {getNextRankOptions(student.belt_rank).map(rank => (
                                    <option key={rank.rank_id} value={rank.rank}>
                                      {rank.rank}
                                    </option>
                                  ))}
                                </Form.Select>
                              )}
                            </td>
                            <td>
                              {student.is_readonly ? (
                                <span className="text-muted">-</span>
                              ) : (
                                <Form.Control
                                  type="number"
                                  min="0"
                                  max={student.updated_rank ? 
                                    (beltRanks.find(r => r.rank === student.updated_rank)?.max_adult_stripes || 0) : 0
                                  }
                                  value={student.updated_stripes}
                                  onChange={(e) => handleStripesChange(student.student_id, e.target.value)}
                                  disabled={!student.updated_rank}
                                />
                              )}
                            </td>
                            <td>
                              {student.is_readonly ? (
                                <span className="text-muted">-</span>
                              ) : (
                                <Form.Check
                                  type="checkbox"
                                  checked={student.updated_is_junior}
                                  onChange={(e) => handleIsJuniorChange(student.student_id, e.target.checked)}
                                  disabled={!student.updated_rank}
                                />
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              )}
            </div>
          </Tab>
          <Tab eventKey="bulk-search" title="Bulk Search">
            <div className="p-3">
              {/* Search Filters */}
              <div className="card bg-dark mb-4">
                <div className="card-body">
                  <h5 className="card-title text-light mb-3">Search Filters</h5>
                  <div className="row g-3">
                    <div className="col-md-3">
                      <input
                        type="text"
                        name="first_name"
                        value={bulkSearchFilters.first_name}
                        onChange={handleBulkSearchChange}
                        className="form-control"
                        placeholder="First Name"
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        name="last_name"
                        value={bulkSearchFilters.last_name}
                        onChange={handleBulkSearchChange}
                        className="form-control"
                        placeholder="Last Name"
                      />
                    </div>
                    <div className="col-md-3">
                      <input
                        type="text"
                        name="current_rank"
                        value={bulkSearchFilters.current_rank}
                        onChange={handleBulkSearchChange}
                        className="form-control"
                        placeholder="Rank"
                      />
                    </div>
                    <div className="col-md-3">
                      <select
                        name="is_junior"
                        value={bulkSearchFilters.is_junior}
                        onChange={handleBulkSearchChange}
                        className="form-control"
                      >
                        <option value="all">All Students</option>
                        <option value="yes">Junior Students</option>
                        <option value="no">Adult Students</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <select
                        name="time_since_promotion"
                        value={bulkSearchFilters.time_since_promotion}
                        onChange={handleBulkSearchChange}
                        className="form-control"
                      >
                        <option value="all">All Time Periods</option>
                        <option value="30">30 Days</option>
                        <option value="60">60 Days</option>
                        <option value="90">90 Days</option>
                        <option value="180">6 Months</option>
                        <option value="365">1 Year</option>
                        <option value="730">2 Years</option>
                      </select>
                    </div>
                    <div className="col-md-3">
                      <Form.Label className="text-light">Promotion Date:</Form.Label>
                      <input
                        type="date"
                        value={promotionDate}
                        onChange={(e) => setPromotionDate(e.target.value)}
                        className="form-control"
                      />
                    </div>
                    <div className="col-md-3">
                      <Button
                        variant="primary"
                        onClick={performBulkSearch}
                        disabled={bulkSearchLoading}
                      >
                        {bulkSearchLoading ? (
                          <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Searching...
                          </>
                        ) : (
                          "Search"
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Search Results */}
              {bulkSearchResults.length > 0 && (
                <div className="mt-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4>Search Results ({bulkSearchResults.length} students)</h4>
                    <Button
                      variant="primary"
                      onClick={handleBulkSubmitPromotions}
                      disabled={getBulkPromotionCount() === 0 || submitting}
                    >
                      {submitting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        `Submit Promotions (${getBulkPromotionCount()})`
                      )}
                    </Button>
                  </div>

                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Student Name</th>
                        <th>Belt Rank</th>
                        <th>Stripes</th>
                        <th>Current Is Junior</th>
                        <th>Date of Last Promotion</th>
                        <th>Updated Rank</th>
                        <th>Updated Stripes</th>
                        <th>Updated Is Junior</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bulkSearchResults.map((student) => (
                        <tr key={student.student_id}>
                          <td>{student.student_name}</td>
                          <td>{student.belt_rank}</td>
                          <td>{student.stripes}</td>
                          <td>
                            <span className={`badge ${student.current_is_junior ? 'bg-success' : 'bg-secondary'}`}>
                              {student.current_is_junior ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td>
                            {student.date_last_promotion 
                              ? new Date(student.date_last_promotion).toLocaleDateString()
                              : "Never"
                            }
                          </td>
                          <td>
                            <Form.Select
                              value={student.updated_rank}
                              onChange={(e) => handleBulkRankChange(student.student_id, e.target.value)}
                            >
                              <option value="">Select rank...</option>
                              {getNextRankOptions(student.belt_rank).map(rank => (
                                <option key={rank.rank_id} value={rank.rank}>
                                  {rank.rank}
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td>
                            <Form.Control
                              type="number"
                              min="0"
                              max={student.updated_rank ? 
                                (beltRanks.find(r => r.rank === student.updated_rank)?.max_adult_stripes || 0) : 0
                              }
                              value={student.updated_stripes}
                              onChange={(e) => handleBulkStripesChange(student.student_id, e.target.value)}
                              disabled={!student.updated_rank}
                            />
                          </td>
                          <td>
                            <Form.Check
                              type="checkbox"
                              checked={student.updated_is_junior}
                              onChange={(e) => handleBulkIsJuniorChange(student.student_id, e.target.checked)}
                              disabled={!student.updated_rank}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </div>
          </Tab>
        </Tabs>
      </div>
    </div>
  );
};

export default Promotions;
